// src/components/dashboard/RiskScoreCard.jsx
// First element on the Dashboard — visible to Free and Pro users.
// Shows the Nuvport Risk Score dial, primary driver, and CTA.

import { useState, useEffect } from 'react'
import RiskScoreDial from './RiskScoreDial'
import { getOrCalculateRiskScore } from '../../lib/riskScore'

function SkeletonDial() {
  return (
    <div style={{
      width: 200, height: 120,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 160, height: 80,
        borderRadius: '80px 80px 0 0',
        background: 'linear-gradient(90deg, #1a2332 25%, #252f3e 50%, #1a2332 75%)',
        backgroundSize: '200% 100%',
        animation: 'rs-shimmer 1.4s infinite',
      }} />
    </div>
  )
}

export default function RiskScoreCard({ userId, holdings, portfolioReady, tier, onSeeWhatChanged, onOpenBilling }) {
  const [riskScore, setRiskScore] = useState(null)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(false)

  useEffect(() => {
    if (!userId || !portfolioReady) {
      setLoading(false)
      return
    }

    setLoading(true)
    getOrCalculateRiskScore(userId, holdings ?? [])
      .then(data => {
        setRiskScore(data)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }, [userId, portfolioReady])

  const calculatedTime = riskScore?.calculated_at
    ? new Date(riskScore.calculated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null

  return (
    <>
      <style>{`
        @keyframes rs-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes rs-fadein {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderTop: '2px solid var(--accent)',
        borderRadius: 'var(--radius-lg)',
        padding: '20px 24px',
        marginBottom: 20,
        animation: 'rs-fadein .3s ease',
      }}>
        {/* Header row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-tertiary)',
            letterSpacing: '.12em',
            textTransform: 'uppercase',
          }}>
            PORTFOLIO RISK · TODAY
          </span>
          {calculatedTime && (
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--text-tertiary)',
              letterSpacing: '.06em',
            }}>
              calculated {calculatedTime}
            </span>
          )}
        </div>

        {/* Main content */}
        {!portfolioReady ? (
          // No portfolio state
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 0',
          }}>
            <span style={{ fontSize: 20 }}>⊙</span>
            <div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--text-secondary)',
                lineHeight: 1.5,
              }}>
                Set your portfolio value to calculate your risk score
              </div>
            </div>
          </div>
        ) : loading ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SkeletonDial />
          </div>
        ) : error || !riskScore ? (
          // Error / no score yet state
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--text-tertiary)',
            padding: '12px 0',
            letterSpacing: '.04em',
          }}>
            Risk score updating — check back shortly
          </div>
        ) : (
          // Full score state
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 32,
            flexWrap: 'wrap',
          }}>
            {/* Dial */}
            <div style={{ flexShrink: 0 }}>
              <RiskScoreDial
                score={riskScore.score}
                previousScore={riskScore.previous_score}
                size={180}
              />
            </div>

            {/* Right side: driver + CTA */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: 'var(--text-tertiary)',
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Primary driver
              </div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                color: 'var(--text-primary)',
                fontWeight: 700,
                marginBottom: 4,
              }}>
                {riskScore.primary_driver}
              </div>
              {riskScore.primary_detail && (
                <div style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  marginBottom: 12,
                }}>
                  {riskScore.primary_detail}
                </div>
              )}

              {/* CTA row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                <button
                  onClick={tier === 'pro' ? onSeeWhatChanged : onOpenBilling}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--accent)',
                    color: 'var(--accent)',
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    cursor: 'pointer',
                    letterSpacing: '.06em',
                    transition: 'all .15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--accent-dim)'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent'
                  }}
                >
                  See what changed →
                </button>

                {tier !== 'pro' && (
                  <span style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 9,
                    color: 'var(--text-tertiary)',
                    border: '1px solid var(--border)',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-sm)',
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                  }}>
                    PRO
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
